import { parseSignature, getTerminalData, analyzeAndGroupCables, getSiglaturaPerStampa, getStandardCableDisplaySiglatura, getStandardCableSideSiglature, getMultipolarLabel } from './cableLogic';
import { withEW3035Settings } from '../machines/EW-3035+L';
import { normalizeColorToCanonical } from './colorUtils';

export const calculateCableQuantities = (cables, settings) => {
  settings = withEW3035Settings(settings);
  const quantities = {};

  cables.forEach(cable => {
    if (cable.poli && Array.isArray(cable.poli)) {
      // Cavi Multipolari
      const poles = cable.poli.length;
      const section = cable.poli[0]?.sezione || 0.5;
      const multipolarLabel = getMultipolarLabel(cable.poli, settings.useAWG);
      const poleColors = cable.poli.map((pole) => pole.colore).filter(Boolean);
      const internalCode = String(cable.catalogAssignment?.codiceInterno || '').trim();
      const key = internalCode ? `catalog:${internalCode.toLocaleLowerCase()}` : multipolarLabel;

      if (!quantities[key]) {
        quantities[key] = {
          colore: multipolarLabel,
          sezione: section,
          poles,
          poleColors,
          count: 0,
          totalLength: 0,
          isMultipolar: true,
          codiceInterno: internalCode,
          descrizione: internalCode ? String(cable._catalogDescription || '') : ''
        };
      }
      quantities[key].count += 1;
      quantities[key].totalLength += cable.lunghezza || 0;
    } else {
      // Cavi Standard
      const section = cable.sezione;
      const internalCode = String(cable.catalogAssignment?.codiceInterno || '').trim();
      const key = internalCode ? `catalog:${internalCode.toLocaleLowerCase()}` : `${cable.colore} ${section}`;

      if (!quantities[key]) {
        quantities[key] = {
          colore: cable.colore,
          sezione: section,
          count: 0,
          totalLength: 0,
          isMultipolar: false,
          codiceInterno: internalCode,
          descrizione: internalCode ? String(cable._catalogDescription || '') : ''
        };
      }
      quantities[key].count += 1;
      quantities[key].totalLength += cable.lunghezza || 0;
    }
  });

  return Object.values(quantities)
    .sort((a, b) => {
      // Prima i cavi standard, poi i multipolari
      if (a.isMultipolar !== b.isMultipolar) {
        return a.isMultipolar ? 1 : -1;
      }

      if (a.sezione !== b.sezione) {
        return a.sezione - b.sezione;
      }

      // Poi per colore/nome
      return a.colore.localeCompare(b.colore);
    });
};

export const calculateTerminalQuantities = (cables, settings, terminali) => {
  settings = withEW3035Settings(settings);
  const quantities = {};

  const addTerminal = (terminal, quantity = 1) => {
    if (!terminal || terminal.trim() === '') return;

    if (!quantities[terminal]) {
      quantities[terminal] = {
        terminal: terminal,
        count: 0,
        terminalData: getTerminalData ? getTerminalData(terminal, settings, terminali) : null
      };
    }
    quantities[terminal].count += quantity;
  };

  // Separa cavi standard e multipolari
  const standardCables = cables.filter(cable => !(cable.poli && Array.isArray(cable.poli)));
  const multipolarCables = cables.filter(cable => cable.poli && Array.isArray(cable.poli));

  // PROCESSA CAVI MULTIPOLARI
  multipolarCables.forEach(cable => {
    cable.poli.forEach(polo => {
      addTerminal(polo.terminaleA, 1);
      addTerminal(polo.terminaleB, 1);
    });
  });

  // PROCESSA CAVI STANDARD
  if (standardCables.length > 0 && analyzeAndGroupCables) {
    analyzeAndGroupCables(standardCables, settings);

    const singleCables = standardCables.filter(c => c.type === 'single');
    const multipleCables = standardCables.filter(c => c.type === 'multiple');

    // Per cavi single: conta normalmente
    singleCables.forEach(cable => {
      if (cable.terminaleA && cable.terminaleA.trim() !== '') {
        addTerminal(cable.terminaleA, 1);
      }
      if (cable.terminaleB && cable.terminaleB.trim() !== '') {
        addTerminal(cable.terminaleB, 1);
      }
    });

    // Per cavi multiple: logica più sofisticata
    if (multipleCables.length > 0) {
      const realGroups = analyzeAndGroupCables(multipleCables, settings);

      realGroups.forEach(group => {
        if (!group.isSingle && group.cables.length > 1) {
          const connectionMapA = {};
          const connectionMapB = {};

          group.cables.forEach(cable => {
            // Lato A
            if (cable.terminaleA && cable.terminaleA.trim() !== '' &&
              cable.collegamentoA && cable.collegamentoA.trim() !== '') {
              if (!connectionMapA[cable.collegamentoA]) {
                connectionMapA[cable.collegamentoA] = new Set();
              }
              connectionMapA[cable.collegamentoA].add(cable.terminaleA);
            }

            // Lato B  
            if (cable.terminaleB && cable.terminaleB.trim() !== '' &&
              cable.collegamentoB && cable.collegamentoB.trim() !== '') {
              if (!connectionMapB[cable.collegamentoB]) {
                connectionMapB[cable.collegamentoB] = new Set();
              }
              connectionMapB[cable.collegamentoB].add(cable.terminaleB);
            }
          });

          // Conta terminali
          Object.values(connectionMapA).forEach(terminalsSet => {
            terminalsSet.forEach(terminal => {
              addTerminal(terminal, 1);
            });
          });

          Object.values(connectionMapB).forEach(terminalsSet => {
            terminalsSet.forEach(terminal => {
              addTerminal(terminal, 1);
            });
          });
        }
      });
    }
  } else {
    // Fallback alla logica semplice se analyzeAndGroupCables non disponibile
    standardCables.forEach(cable => {
      addTerminal(cable.terminaleA);
      addTerminal(cable.terminaleB);
    });
  }

  // ORDINAMENTO PERSONALIZZATO (come nel codice originale)
  return Object.entries(quantities)
    .sort((a, b) => {
      const getTerminalOrder = (terminal) => {
        if (terminal.startsWith('PS')) return 1;
        if (terminal.startsWith('PD')) return 2;
        if (terminal.startsWith('FS') || terminal.startsWith('FC') || terminal.startsWith('FA')) return 3;
        if (terminal.startsWith('FR') || terminal.startsWith('FB') || terminal.startsWith('FY')) return 4;
        if (terminal.startsWith('OR') || terminal.startsWith('OB') || terminal.startsWith('OY')) return 5;
        if (terminal.startsWith('ML')) return 6;
        if (terminal === 'TR' || terminal === 'TB' || terminal === 'TY') return 7;
        if (terminal.startsWith('SP')) return 8;
        if (terminal === 'NS') return 9;
        return 10;
      };

      const orderA = getTerminalOrder(a[1].terminal);
      const orderB = getTerminalOrder(b[1].terminal);

      if (orderA !== orderB) {
        return orderA - orderB;
      }
      return a[1].terminal.localeCompare(b[1].terminal);
    })
    .map(([terminal, data]) => ({ terminal, count: data.count, terminalData: data.terminalData }));
};

export const calculateSheathQuantities = (cables, availableSheaths = []) => {
  const quantities = {};
  const processedSheathIds = new Set();

  cables.forEach(cable => {
    // Se cable.guaina è un ID
    if (cable.guaina) {
      // Normalizza ID per evitare duplicati (es. "1" vs 1)
      const sheathId = String(cable.guaina);

      // Se questa guaina è già stata processata, saltala
      if (processedSheathIds.has(sheathId)) return;

      // Aggiungi ai processati
      processedSheathIds.add(sheathId);

      // Cerca la guaina in availableSheaths
      const sheath = availableSheaths.find(s => String(s.id) === sheathId);

      if (sheath) {
        const type = sheath.type || 'PVC'; // Default type if missing
        const diameter = sheath.diameter;
        // Raggruppa per Tipo + Diametro
        const key = `${type}-${diameter}`;

        if (!quantities[key]) {
          quantities[key] = {
            type: type,
            diameter: diameter,
            count: 0,
            totalLength: 0
          };
        }

        quantities[key].count += 1;
        // La lunghezza totale è calcolata come somma delle singole guaine uniche
        quantities[key].totalLength += (sheath.length || 0);
      }
    }
  });

  // Ordina prima per Tipo, poi per Diametro
  return Object.values(quantities).sort((a, b) => {
    if (a.type !== b.type) return a.type.localeCompare(b.type);
    return parseFloat(a.diameter) - parseFloat(b.diameter);
  });
};

export const calculateSiglatureAndMorsetti = (cables, settings) => {
  settings = withEW3035Settings(settings);
  // Se mancano le funzioni helper, restituisci struttura vuota
  if (!analyzeAndGroupCables) {
    return {
      section25_6: [],
      section05_15: [],
      sectionAbove6: [],
      morsetti: []
    };
  }

  const morsettiResults = calculateMorsetti(cables, settings);

  const isEarthCable = (cable) => {
    return normalizeColorToCanonical(cable.colore) === 'YE/GN';
  };

  // ✅ NUOVA: Funzione per verificare se un polo è di terra
  const isEarthPolo = (polo) => {
    return normalizeColorToCanonical(polo.colore) === 'YE/GN';
  };

  const shouldIncludeInSiglature = (cable, minSection, maxSection) => {
    const sezioneInMM = cable.sezione; // la sezione è sempre memorizzata in mm²
    const isEarthCable = normalizeColorToCanonical(cable.colore) === 'YE/GN';

    return sezioneInMM >= minSection &&
      sezioneInMM <= maxSection &&
      !isEarthCable;
  };

  // ✅ NUOVA: Funzione per verificare se un polo deve essere incluso
  const shouldIncludePoloInSiglature = (polo, minSection, maxSection) => {
    const sezioneInMM = polo.sezione; // la sezione è sempre memorizzata in mm²
    const isEarth = isEarthPolo(polo);

    return sezioneInMM >= minSection &&
      sezioneInMM <= maxSection &&
      !isEarth;
  };

  const createSiglaturaAdder = (results) => {
    return (sig, representativeCable, quantity, isFromPolo = false) => {
      const sections = [
        { data: results.section25_6, min: 2.5, max: 6 },
        { data: results.section05_15, min: 0, max: 2 },
        { data: results.sectionAbove6, min: 6.01, max: 100 }
      ];

      sections.forEach(section => {
        let shouldInclude = false;

        if (isFromPolo) {
          // ✅ Per i poli, usa la funzione specifica
          shouldInclude = shouldIncludePoloInSiglature(representativeCable, section.min, section.max);
        } else {
          // Per i cavi standard, usa la logica esistente
          shouldInclude = shouldIncludeInSiglature(representativeCable, section.min, section.max);
        }

        if (shouldInclude) {
          if (!section.data[sig]) {
            section.data[sig] = {
              quantity: 0,
              colore: representativeCable.colore,
              sezione: representativeCable.sezione
            };
          }
          section.data[sig].quantity += quantity;
        }
      });
    };
  };

  const initializeResults = () => ({
    section25_6: {},
    section05_15: {},
    sectionAbove6: {}
  });

  // Conteggio modalità Siglatura
  const calculateSiglaturaFromSiglatura = () => {
    const results = initializeResults();
    const addSiglatura = createSiglaturaAdder(results);

    // Separa cavi standard e multipolari
    const standardCables = cables.filter(cable => !(cable.poli && Array.isArray(cable.poli)));
    const multipolarCables = cables.filter(cable => cable.poli && Array.isArray(cable.poli));

    multipolarCables.forEach(cable => {
      cable.poli.forEach(polo => {
        if (polo.siglaturaA && String(polo.siglaturaA).trim() !== '') {
          addSiglatura(polo.siglaturaA, polo, 1, true);
        }
        if (polo.siglaturaB && String(polo.siglaturaB).trim() !== '') {
          addSiglatura(polo.siglaturaB, polo, 1, true);
        }
      });
    });

    const cablesWithSiglatura = standardCables.filter(cable => {
      const sig = getStandardCableDisplaySiglatura(cable);
      const sigStr = String(sig);
      return sig != null && sigStr.trim() !== '' && sigStr.trim() !== '-';
    });

    const singleCables = cablesWithSiglatura.filter(c => c.type === 'single');
    const multipleCables = cablesWithSiglatura.filter(c => c.type === 'multiple');

    singleCables.forEach(cable => {
      if (!isEarthCable(cable)) {
        const { siglaturaA, siglaturaB } = getStandardCableSideSiglature(cable);
        if (siglaturaA && siglaturaB && siglaturaA === siglaturaB) {
          addSiglatura(siglaturaA, cable, 2);
        } else {
          if (siglaturaA) addSiglatura(siglaturaA, cable, 1);
          if (siglaturaB) addSiglatura(siglaturaB, cable, 1);
        }
      }
    });

    // Processa cavi multiple (logica esistente)
    if (multipleCables.length > 0) {
      const multipleGroupsBySiglatura = {};
      multipleCables.forEach(cable => {
        const sig = getStandardCableDisplaySiglatura(cable);
        if (!multipleGroupsBySiglatura[sig]) {
          multipleGroupsBySiglatura[sig] = [];
        }
        multipleGroupsBySiglatura[sig].push(cable);
      });

      Object.entries(multipleGroupsBySiglatura).forEach(([siglatura, cablesWithSameSig]) => {
        const groupsBySize = {};
        cablesWithSameSig.forEach(cable => {
          const groupKey = `${siglatura}-${cable.groupSize}`;
          if (!groupsBySize[groupKey]) {
            groupsBySize[groupKey] = [];
          }
          groupsBySize[groupKey].push(cable);
        });

        Object.entries(groupsBySize).forEach(([groupKey, groupCables]) => {
          const [, groupSize] = groupKey.split('-');
          // La sezione è sempre in mm²: la maggiore è sempre il Math.max
          const maxSectionInGroup = Math.max(...groupCables.map(c => c.sezione));
          const allSectionsInGroup = [...new Set(groupCables.map(c => c.sezione))].sort((a, b) => b - a);

          const processSection = (cablesInSection) => {
            if (cablesInSection.length > 0 && !isEarthCable(cablesInSection[0])) {
              const maxSectionInThisFascia = Math.max(...cablesInSection.map(c => c.sezione));

              const representativeCable = cablesInSection.find(c => c.sezione === maxSectionInThisFascia);

              let quantity;
              if (allSectionsInGroup.length > 1) {
                const isMaxSection = maxSectionInThisFascia === maxSectionInGroup;
                quantity = isMaxSection ? cablesInSection.length + 1 : cablesInSection.length;
              } else {
                quantity = parseInt(groupSize) + 1;
              }

              addSiglatura(siglatura, representativeCable, quantity);
            }
          };

          const sections = [
            { min: 0, max: 2 },
            { min: 2.5, max: 6 },
            { min: 6.01, max: 100 }
          ];

          sections.forEach(section => {
            const cablesInSection = groupCables.filter(c =>
              shouldIncludeInSiglature(c, section.min, section.max)
            );
            processSection(cablesInSection);
          });
        });
      });
    }

    return {
      section25_6: groupIntoSequences(results.section25_6),
      section05_15: groupIntoSequences(results.section05_15),
      sectionAbove6: groupIntoSequences(results.sectionAbove6)
    };
  };

  // Conteggio modalità Collegamento
  const calculateSiglaturaFromCollegamenti = () => {
    const results = initializeResults();
    const addSiglatura = createSiglaturaAdder(results);

    // Separa cavi standard e multipolari
    const standardCables = cables.filter(cable => !(cable.poli && Array.isArray(cable.poli)));
    const multipolarCables = cables.filter(cable => cable.poli && Array.isArray(cable.poli));

    multipolarCables.forEach(cable => {
      cable.poli.forEach(polo => {
        const siglaturaA = extractSiglaturaFromCollegamento(polo.collegamentoA);
        const siglaturaB = extractSiglaturaFromCollegamento(polo.collegamentoB);

        // Conta siglatura A se presente
        if (siglaturaA && String(siglaturaA).trim() !== '') {
          addSiglatura(siglaturaA, polo, 1, true);
        }
        // Conta siglatura B se presente (anche se uguale ad A)
        if (siglaturaB && String(siglaturaB).trim() !== '') {
          addSiglatura(siglaturaB, polo, 1, true);
        }
      });
    });

    const cablesWithCollegamenti = standardCables.filter(cable => {
      const siglature = getSiglaturaPerStampa(cable, settings);
      const sigArray = Array.isArray(siglature) ? siglature : [siglature];
      return sigArray.some(sig => sig && String(sig).trim() !== '');
    });

    const subGroups = analyzeAndGroupCables(cablesWithCollegamenti, settings);

    subGroups.forEach(group => {
      if (group.isSingle) {
        const cable = group.cables[0];
        const siglature = getSiglaturaPerStampa(cable, settings);
        const sigArray = Array.isArray(siglature) ? siglature : [siglature];

        sigArray.forEach(sig => {
          if (sig && String(sig).trim() !== '') {
            addSiglatura(sig, cable, 1);
          }
        });
      } else {
        const connectionMap = {};
        group.cables.forEach(cable => {
          const siglature = getSiglaturaPerStampa(cable, settings);
          const sigArray = Array.isArray(siglature) ? siglature : [siglature];

          sigArray.forEach(sig => {
            if (sig && String(sig).trim() !== '') {
              if (!connectionMap[sig]) {
                connectionMap[sig] = [];
              }
              connectionMap[sig].push(cable);
            }
          });
        });

        Object.entries(connectionMap).forEach(([sig, cablesWithThisConnection]) => {
          // La sezione è sempre in mm²: la maggiore è sempre il Math.max
          const maxSection = Math.max(...cablesWithThisConnection.map(c => c.sezione));

          const representativeCable = cablesWithThisConnection.find(c => c.sezione === maxSection);
          addSiglatura(sig, representativeCable, 1);
        });
      }
    });

    return {
      section25_6: groupIntoSequences(results.section25_6),
      section05_15: groupIntoSequences(results.section05_15),
      sectionAbove6: groupIntoSequences(results.sectionAbove6)
    };
  };

  let siglaturaResults;

  // Determina quale logica usare
  const standardCablesWithSiglatura = cables.filter(cable => {
    if (cable.poli && Array.isArray(cable.poli)) return false; // Escludi multipolari

    if (!settings.marcaturePerCollegamento) {
      const sig = getStandardCableDisplaySiglatura(cable);
      return sig &&
        typeof sig === 'string' &&
        sig.trim() !== '' &&
        sig.trim() !== '-';
    } else {
      const siglaturaStampa = getSiglaturaPerStampa(cable, settings);
      const sigArray = Array.isArray(siglaturaStampa) ? siglaturaStampa : [siglaturaStampa];
      return sigArray.some(sig => sig && String(sig).trim() !== '');
    }
  });

  // ✅ AGGIUNGI CONTROLLO PER MULTIPOLARI
  const multipolarCablesWithSiglatura = cables.filter(cable => {
    if (!(cable.poli && Array.isArray(cable.poli))) return false;

    if (!settings.marcaturePerCollegamento) {
      // Modalità siglatura: controlla se almeno un polo ha siglatura
      return cable.poli.some(polo =>
        (polo.siglaturaA && String(polo.siglaturaA).trim() !== '') ||
        (polo.siglaturaB && String(polo.siglaturaB).trim() !== '')
      );
    } else {
      // Modalità collegamento: controlla se almeno un polo ha collegamenti validi
      return cable.poli.some(polo => {
        const sigA = extractSiglaturaFromCollegamento(polo.collegamentoA);
        const sigB = extractSiglaturaFromCollegamento(polo.collegamentoB);
        return (sigA && String(sigA).trim() !== '') || (sigB && String(sigB).trim() !== '');
      });
    }
  });

  if (standardCablesWithSiglatura.length > 0 || multipolarCablesWithSiglatura.length > 0) {
    if (settings.marcaturePerCollegamento) {
      siglaturaResults = calculateSiglaturaFromCollegamenti();
    } else {
      siglaturaResults = calculateSiglaturaFromSiglatura();
    }
  } else {
    siglaturaResults = {
      section25_6: [],
      section05_15: [],
      sectionAbove6: []
    };
  }

  return {
    ...siglaturaResults,
    morsetti: morsettiResults
  };
};

const calculateMorsetti = (cables, settings) => {
  const results = { morsetti: {} };

  // ==================== FUNZIONI HELPER MORSETTI ====================

  const extractMorsettiBase = (conn) => {
    if (!conn) return [];

    const morsettoMatch = /MORS\.?\s+([^\s▲▼]+)/i.exec(conn);
    if (morsettoMatch) {
      return [morsettoMatch[1]];
    }

    const peMatch = /^PE(\s+([^\s▲▼]+))?$/i.exec(conn);
    if (peMatch) {
      const result = peMatch[2] || 'PE';
      return [result];
    }

    return [];
  };

  const addMorsetto = (morsetto, quantity = 1) => {
    if (!results.morsetti[morsetto]) {
      results.morsetti[morsetto] = {
        quantity: 0,
        colore: '',
        sezione: 0
      };
    }
    results.morsetti[morsetto].quantity += quantity;
  };
  // Funzione per raccogliere morsetti con gestione UP/DOWN
  const collectMorsettiWithUpDown = (cablesArray) => {
    const morsettiByNumber = {}; // { "1": { connections: Set(), hasUp: bool, hasDown: bool, totalUsage: 0 } }

    cablesArray.forEach(cable => {
      [cable.collegamentoA, cable.collegamentoB].forEach(conn => {
        if (!conn) return;

        const baseMorsetti = extractMorsettiBase(conn);
        baseMorsetti.forEach(baseNumber => {
          if (!morsettiByNumber[baseNumber]) {
            morsettiByNumber[baseNumber] = {
              connections: new Set(),
              hasUp: false,
              hasDown: false,
              totalUsage: 0
            };
          }

          // Registra la connessione completa (con ▲▼)
          morsettiByNumber[baseNumber].connections.add(conn);
          morsettiByNumber[baseNumber].totalUsage++;

          // Rileva UP/DOWN
          if (conn.includes('▲')) {
            morsettiByNumber[baseNumber].hasUp = true;
          } else if (conn.includes('▼')) {
            morsettiByNumber[baseNumber].hasDown = true;
          } else {
            // Se non specificato, considera UP
            morsettiByNumber[baseNumber].hasUp = true;
          }
        });
      });
    });

    return morsettiByNumber;
  };

  // ==================== PROCESSAMENTO MORSETTI ====================

  // Assicurati che i cavi abbiano i tipi corretti
  analyzeAndGroupCables(cables, settings);

  const allSingleCables = cables.filter(c => c.type === 'single');
  const allMultipleCables = cables.filter(c => c.type === 'multiple');

  const singleMorsetti = collectMorsettiWithUpDown(allSingleCables);

  Object.entries(singleMorsetti).forEach(([morsettoNum, data]) => {
    if (data.hasUp && data.hasDown) {
      addMorsetto(morsettoNum, 1);
    } else {
      addMorsetto(morsettoNum, data.totalUsage);
    }
  });

  let multiplesWithValidIdentifier = [];
  let multiplesWithoutValidIdentifier = [];

  if (!settings.marcaturePerCollegamento) {
    multiplesWithValidIdentifier = allMultipleCables.filter(cable => {
      const sig = getStandardCableDisplaySiglatura(cable);
      const sigStr = String(sig);
      return sig != null && sigStr.trim() !== '' && sigStr.trim() !== '-';
    });

    multiplesWithoutValidIdentifier = allMultipleCables.filter(cable => {
      const sig = getStandardCableDisplaySiglatura(cable);
      const sigStr = String(sig);
      return !sig || sigStr.trim() === '' || sigStr.trim() === '-';
    });
  } else {
    multiplesWithValidIdentifier = allMultipleCables.filter(cable => {
      const siglature = getSiglaturaPerStampa(cable, settings);
      const sigArray = Array.isArray(siglature) ? siglature : [siglature];
      return sigArray.some(sig => sig && String(sig).trim() !== '');
    });

    multiplesWithoutValidIdentifier = allMultipleCables.filter(cable => {
      const siglature = getSiglaturaPerStampa(cable, settings);
      const sigArray = Array.isArray(siglature) ? siglature : [siglature];
      return !sigArray.some(sig => sig && String(sig).trim() !== '');
    });
  }

  if (multiplesWithValidIdentifier.length > 0) {
    const multipleGroups = {};
    multiplesWithValidIdentifier.forEach(cable => {
      let groupKey;
      if (!settings.marcaturePerCollegamento) {
        groupKey = `${String(getStandardCableDisplaySiglatura(cable))}-${cable.groupSize}`;
      } else {
        const siglature = getSiglaturaPerStampa(cable, settings);
        const sigArray = Array.isArray(siglature) ? siglature : [siglature];
        const mainSig = sigArray[0] || 'UNKNOWN';
        groupKey = `${mainSig}-${cable.groupSize}`;
      }

      if (!multipleGroups[groupKey]) {
        multipleGroups[groupKey] = [];
      }
      multipleGroups[groupKey].push(cable);
    });

    Object.entries(multipleGroups).forEach(([groupKey, groupCables]) => {
      const groupMorsetti = collectMorsettiWithUpDown(groupCables);
      Object.entries(groupMorsetti).forEach(([morsettoNum, data]) => {
        addMorsetto(morsettoNum, 1);
      });
    });
  }

  if (multiplesWithoutValidIdentifier.length > 0) {
    const groupsBySizeOnly = {};
    multiplesWithoutValidIdentifier.forEach(cable => {
      const groupKey = `no-identifier-${cable.groupSize}`;
      if (!groupsBySizeOnly[groupKey]) {
        groupsBySizeOnly[groupKey] = [];
      }
      groupsBySizeOnly[groupKey].push(cable);
    });

    Object.entries(groupsBySizeOnly).forEach(([groupKey, groupCables]) => {
      const groupMorsetti = collectMorsettiWithUpDown(groupCables);
      Object.entries(groupMorsetti).forEach(([morsettoNum, data]) => {
        addMorsetto(morsettoNum, 1);
      });
    });
  }

  return groupIntoSequences(results.morsetti);
};

// ==================== FUNZIONI HELPER CALCOLO SIGLATURE ====================

const extractSiglaturaFromCollegamento = (collegamento) => {
  if (!collegamento) return '';

  // Pulisci frecce e parentesi
  const cleaned = collegamento.replace(/▲|▼/g, '').replace(/\s*\([^)]*\)/g, '').trim();

  // Applica la stessa logica di extractMorsettiBase
  const morsettoMatch = /MORS\.?\s+([^\s▲▼]+)/i.exec(cleaned);
  const peMatch = /PE(\s+([^\s▲▼]+))?/i.exec(cleaned);

  if (morsettoMatch) return morsettoMatch[1];
  if (peMatch) return '';

  return cleaned;
};

// Raggruppa siglature in sequenze
const groupIntoSequences = (signaturesData) => {
  const entries = Object.entries(signaturesData);

  if (entries.length === 0) return [];

  // ✅ STEP 1: Parse una sola volta e ordina
  const parsedEntries = entries.map(([sig, data]) => ({
    signature: sig,
    parsed: parseSignature(sig),
    data: data
  })).sort((a, b) => {
    // Ordinamento per pattern con trattino
    if (a.parsed.hasPattern && b.parsed.hasPattern) {
      if (a.parsed.patternBase !== b.parsed.patternBase) {
        return a.parsed.patternBase.localeCompare(b.parsed.patternBase);
      }
      return compareAlphanumericSuffix(a.parsed.patternSuffix, b.parsed.patternSuffix);
    }

    // Se uno ha pattern e l'altro no, pattern prima
    if (a.parsed.hasPattern !== b.parsed.hasPattern) {
      return a.parsed.hasPattern ? -1 : 1;
    }

    // Ordinamento per pattern normali
    if (a.parsed.letter !== b.parsed.letter) {
      return a.parsed.letter.localeCompare(b.parsed.letter);
    }
    if (a.parsed.integer !== b.parsed.integer) {
      return a.parsed.integer - b.parsed.integer;
    }
    return a.parsed.decimal - b.parsed.decimal;
  });

  // ✅ STEP 2: Raggruppa in una sola passata
  const groups = [];
  let currentGroup = [parsedEntries[0]];

  for (let i = 1; i < parsedEntries.length; i++) {
    const prev = currentGroup[currentGroup.length - 1];
    const current = parsedEntries[i];

    // Verifica se può unirsi al gruppo corrente
    const canJoin = (
      prev.data.quantity === current.data.quantity &&
      areConsecutive(prev.parsed, current.parsed)
    );

    if (canJoin) {
      currentGroup.push(current);
    } else {
      groups.push(currentGroup);
      currentGroup = [current];
    }
  }
  groups.push(currentGroup);

  // ✅ STEP 3: Applica raggruppamento cross-letter SEMPLIFICATO
  return applyCrossLetterGrouping(groups);
};

// ✅ Funzione consecutività ottimizzata
const areConsecutive = (parsed1, parsed2) => {
  // Gestione pattern con trattino
  if (parsed1.hasPattern && parsed2.hasPattern) {
    // Entrambi hanno pattern con trattino
    if (parsed1.patternBase === parsed2.patternBase) {
      // Stessa base, controlla consecutività del suffisso
      return areAlphanumericConsecutive(parsed1.patternSuffix, parsed2.patternSuffix);
    }
    return false;
  }

  // Se uno ha pattern e l'altro no, non sono consecutivi
  if (parsed1.hasPattern !== parsed2.hasPattern) {
    return false;
  }

  // Logica esistente per pattern normali
  if (parsed1.letter !== parsed2.letter) return false;

  // Controllo priorità: prima decimali, poi interi
  if (parsed1.decimal !== parsed2.decimal) {
    return (parsed2.decimal - parsed1.decimal) === 1;
  }

  return (parsed2.integer - parsed1.integer) === 1;
};

// Verifica consecutività per suffix alfanumerici
const areAlphanumericConsecutive = (suffix1, suffix2) => {
  // Caso 1: Entrambi numeri puri
  const num1 = parseInt(suffix1);
  const num2 = parseInt(suffix2);
  if (!isNaN(num1) && !isNaN(num2)) {
    return (num2 - num1) === 1;
  }

  // Caso 2: Pattern alfanumerici (es. "A1" vs "A2")
  const alphaNumPattern = /^([A-Za-z]+)(\d+)$/;
  const match1 = suffix1.match(alphaNumPattern);
  const match2 = suffix2.match(alphaNumPattern);

  if (match1 && match2) {
    const [, letter1, num1] = match1;
    const [, letter2, num2] = match2;

    // Stesse lettere, numeri consecutivi
    if (letter1 === letter2) {
      return (parseInt(num2) - parseInt(num1)) === 1;
    }

    // Lettere consecutive, numeri uguali
    if (parseInt(num1) === parseInt(num2) && letter1.length === 1 && letter2.length === 1) {
      const charCode1 = letter1.charCodeAt(0);
      const charCode2 = letter2.charCodeAt(0);
      return (charCode2 - charCode1) === 1;
    }
  }

  // Caso 3: Solo lettere
  if (/^[A-Za-z]+$/.test(suffix1) && /^[A-Za-z]+$/.test(suffix2)) {
    if (suffix1.length === 1 && suffix2.length === 1) {
      const charCode1 = suffix1.charCodeAt(0);
      const charCode2 = suffix2.charCodeAt(0);
      return (charCode2 - charCode1) === 1;
    }
  }

  return false;
};

// ✅ NUOVA FUNZIONE: Ordinamento per suffix alfanumerici
const compareAlphanumericSuffix = (suffix1, suffix2) => {
  // Caso 1: Entrambi numeri puri
  const num1 = parseInt(suffix1);
  const num2 = parseInt(suffix2);
  if (!isNaN(num1) && !isNaN(num2)) {
    return num1 - num2;
  }

  // Caso 2: Pattern alfanumerici
  const alphaNumPattern = /^([A-Za-z]+)(\d+)$/;
  const match1 = suffix1.match(alphaNumPattern);
  const match2 = suffix2.match(alphaNumPattern);

  if (match1 && match2) {
    const [, letter1, num1] = match1;
    const [, letter2, num2] = match2;

    // Prima ordina per lettera, poi per numero
    if (letter1 !== letter2) {
      return letter1.localeCompare(letter2);
    }
    return parseInt(num1) - parseInt(num2);
  }

  // Fallback: ordinamento lessicografico
  return suffix1.localeCompare(suffix2);
};

// ✅ VERSIONE SEMPLIFICATA (meno complessa):
const applyCrossLetterGrouping = (groups) => {
  const result = [];
  const processed = new Set();

  groups.forEach((group, index) => {
    if (processed.has(index)) return;

    // ✅ Cerca pattern simili in una sola passata
    const similarGroups = [group];
    const groupPattern = getGroupPattern(group);

    for (let i = index + 1; i < groups.length; i++) {
      if (processed.has(i)) continue;

      const otherGroup = groups[i];
      const otherPattern = getGroupPattern(otherGroup);

      // ✅ Pattern matching semplificato
      if (isSamePattern(groupPattern, otherPattern) &&
        areLettersConsecutive(similarGroups, otherGroup)) {
        similarGroups.push(otherGroup);
        processed.add(i);
      }
    }

    processed.add(index);

    // ✅ Combina risultato
    if (similarGroups.length > 1) {
      result.push(combineSimilarGroups(similarGroups));
    } else {
      result.push(convertGroupToResult(group));
    }
  });

  return result;
};

// ✅ HELPER SEMPLIFICATI:
const getGroupPattern = (group) => {
  const first = group[0].parsed;
  const last = group[group.length - 1].parsed;

  return {
    // Campi per pattern con trattino
    hasPattern: first.hasPattern,
    patternBase: first.patternBase,
    startPatternSuffix: first.patternSuffix,
    endPatternSuffix: last.patternSuffix,

    // Campi esistenti
    startNum: first.integer,
    endNum: last.integer,
    startDec: first.decimal,
    endDec: last.decimal,
    quantity: group[0].data.quantity,
    groupSize: group.length
  };
};

const isSamePattern = (pattern1, pattern2) => {
  // Confronto per pattern con trattino
  if (pattern1.hasPattern && pattern2.hasPattern) {
    return (
      pattern1.patternBase === pattern2.patternBase &&
      pattern1.startPatternSuffix === pattern2.startPatternSuffix &&
      pattern1.endPatternSuffix === pattern2.endPatternSuffix &&
      pattern1.quantity === pattern2.quantity &&
      pattern1.groupSize === pattern2.groupSize
    );
  }

  // Logica esistente per pattern normali
  return (
    pattern1.hasPattern === pattern2.hasPattern &&
    pattern1.startNum === pattern2.startNum &&
    pattern1.endNum === pattern2.endNum &&
    pattern1.startDec === pattern2.startDec &&
    pattern1.endDec === pattern2.endDec &&
    pattern1.quantity === pattern2.quantity &&
    pattern1.groupSize === pattern2.groupSize
  );
};

const areLettersConsecutive = (existingGroups, newGroup) => {
  const allLetters = [
    ...existingGroups.map(g => g[0].parsed.letter),
    newGroup[0].parsed.letter
  ].sort();

  // ✅ Verifica consecutività in una sola passata
  for (let i = 1; i < allLetters.length; i++) {
    if (allLetters[i].charCodeAt(0) - allLetters[i - 1].charCodeAt(0) !== 1) {
      return false;
    }
  }
  return true;
};

const combineSimilarGroups = (groups) => {
  const firstGroup = groups[0];
  const lastGroup = groups[groups.length - 1];

  return {
    inizio: firstGroup[0].signature,
    ripetizioni: firstGroup[0].data.quantity,
    fine: firstGroup.length > 1 ? lastGroup[lastGroup.length - 1].signature : lastGroup[0].signature,
    quantita: groups.reduce((sum, group) =>
      sum + group.reduce((groupSum, item) => groupSum + item.data.quantity, 0), 0
    ),
    colore: firstGroup[0].data.colore,
    sezione: firstGroup[0].data.sezione
  };
};

const convertGroupToResult = (group) => {
  const totalQuantity = group.reduce((sum, item) => sum + item.data.quantity, 0);

  return {
    inizio: group[0].signature,
    ripetizioni: group[0].data.quantity,
    fine: group.length > 1 ? group[group.length - 1].signature : '',
    quantita: totalQuantity,
    colore: group[0].data.colore || '',
    sezione: group[0].data.sezione || 0
  };
};
